/** @format */

import type {
  StoreHours,
  HeaderLocation,
  HeaderStoreSelection,
} from '@shared/data-access/interfaces';
import { createFeatureSelector, createSelector } from '@ngrx/store';

function isHeaderStoreSelection(
  storeInfo: HeaderStoreSelection | null | undefined,
): storeInfo is HeaderStoreSelection {
  return !!storeInfo && 'chain' in storeInfo;
}

/** @description Header Location State Selectors **/
export const selectHeaderLocationState = createFeatureSelector<HeaderLocation>('headerLocation');
export const selectUserStoreName = createSelector(
  selectHeaderLocationState,
  (state) => state?.storeName,
);

export const selectUserStoreNameTitleCase = createSelector(
  selectUserStoreName,
  (storeName: string) =>
    storeName?.toLowerCase().replace(/(?:^|\s)\w/g, (match) => match.toUpperCase()),
);

export const selectUserStoreInfo = createSelector(
  selectHeaderLocationState,
  (state): HeaderStoreSelection | null => state?.storeInfo ?? null,
);

export const selectUserFullStoreInfo = createSelector(
  selectUserStoreInfo,
  (storeInfo): HeaderStoreSelection | null =>
    isHeaderStoreSelection(storeInfo) ? storeInfo : null,
);

export const selectUserStoreNumber = createSelector(selectHeaderLocationState, (state) => {
  const storeInfo = state?.storeInfo;

  if (!storeInfo) {
    return undefined;
  }

  return storeInfo.store;
});

export const selectUserStoreZipcode = createSelector(selectHeaderLocationState, (state) => {
  const storeInfo = state?.storeInfo;

  if (!storeInfo) {
    return undefined;
  }

  return storeInfo.zipcode;
});

export const selectUserStoreNumberAndBaseStoreZip = createSelector(
  selectUserStoreNumber,
  selectUserStoreZipcode,
  (storeNumber, zipcode) => {
    const baseZip = zipcode !== null && zipcode !== undefined ? zipcode.substring(0, 5) : '';

    return {
      location: storeNumber ?? '',
      zip: baseZip,
    };
  },
);

export const selectUserZipCode = createSelector(
  selectHeaderLocationState,
  (state) => state?.zipCode,
);

export const selectNearbyStores = createSelector(
  selectHeaderLocationState,
  (state) => state?.nearbyStores,
);

export const selectCrossChainBopisStoreName = createSelector(
  selectUserFullStoreInfo,
  (storeInfo) => {
    if (storeInfo?.chain === 'GOLF GALAXY') {
      return 'Golf Galaxy - ';
    } else if (storeInfo?.chain === 'DICKS SPORTING GOODS') {
      return "DICK'S - ";
    } else if (storeInfo?.chain === 'PUBLIC LANDS') {
      return 'Public Lands - ';
    }
  },
);

const days: readonly (keyof StoreHours)[] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

export const selectPickupTimeText = createSelector(selectUserStoreInfo, (storeInfo) => {
  if (storeInfo) {
    const d = new Date();
    const day = d.getDay();
    // get current time in 24 hour format
    const currentTime = d.getHours() * 100 + d.getMinutes();

    // split time from string (ex. '0900-2100') to array (ex. ['0900', '2100'])
    const splitTime = storeInfo.storeHours?.[days[day]]?.split('-');
    if (!splitTime) return undefined;
    const openTime = parseInt(splitTime[0], 10);
    const closeTime = parseInt(splitTime[1], 10);
    const oneHour = 100;

    // if current time is before one hour before open time, show ready today
    if (currentTime < openTime - oneHour) {
      return 'Ready today';
      // if current time is before one hour before close time, show ready in one hour
    } else if (currentTime < closeTime - oneHour) {
      return 'Ready within 1 hour';
      // if current time is after one hour before close, show ready tommorow
    } else {
      return 'Ready tomorrow';
    }
  }
});
